/* eslint-disable jsx-a11y/aria-role */
/* eslint-disable react/jsx-props-no-spreading */
import React, { createContext, useContext } from 'react';
import {
  HashRouter,
  Switch,
  Route,
  RouteProps,
  Redirect,
} from 'react-router-dom';
import Login from './pages/login/Login';
import Subscribe from './pages/subscribe/Subscribe';
import NotFound from './pages/notFound/NotFound';
import { useUserData } from './hooks/context/userUserData.hook';
import { AuthService } from './services/Auth/AuthService';
import Dashboard from './pages/dashboard/Dashboard';
import Writers from './pages/writers/actions/list';
import { Users } from './pages/users/actions/list';
import TitlesContent from './pages/titlesCM/titlesListCM/TitlesListContent';
import Categories from './pages/categories/actions/list';
import Contents from './pages/contents/actions/list';
import TitlesWriter from './pages/titlesWriter';
import Reports from './pages/reports/actions/list';
import ProductTypes from './pages/categorization/productTypes/actions/list';
import Products from './pages/categorization/products/actions/list';
import Brands from './pages/categorization/brands/actions/list';
import Tags from './pages/tags';
import SitePages from './pages/sitePages/actions/list';
import SiteCompliance from './pages/siteCompliance/actions/list';
import SiteSettings from './pages/siteSettings/actions/list';

const RoleContext = createContext<Role>('WRITER');

interface IPrivateRouteProps {
  role: Role | Role[];
}

function PrivateRoute({
  component,
  role,
  ...rest
}: RouteProps & IPrivateRouteProps) {
  const userRole = useContext(RoleContext);

  let requiredRole = role;
  if (typeof role === 'string') requiredRole = [role];

  return (
    <Route
      {...rest}
      render={(props) => {
        return AuthService.isLogged() && requiredRole.includes(userRole) ? (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          React.createElement(component!, props)
        ) : (
          <Redirect to="/login" />
        );
      }}
    />
  );
}

interface IRoutePerRoleProps {
  config: { [key in Role]?: string };
  defaultRoute: string;
}

function RoutePerRole({
  config,
  defaultRoute,
  ...rest
}: RouteProps & IRoutePerRoleProps) {
  const userRole = useContext(RoleContext);

  return (
    <Route
      {...rest}
      component={() => <Redirect to={config[userRole] || defaultRoute} />}
    />
  );
}

const CONTENT_MANAGER_ROLES: Role[] = [
  'CONTENTMANAGER',
  'CONTENTDIRECTOR',
  'ADMIN',
];
const WRITERS_ROLES: Role[] = ['WRITER', 'TRANSLATOR', 'REVIEWER', 'EDITOR'];

const Routes: React.FC = () => {
  const [userData] = useUserData();

  return (
    <RoleContext.Provider value={userData?.role}>
      <HashRouter>
        <Switch>
          <RoutePerRole
            exact
            path="/"
            defaultRoute="/dashboard"
            config={{
              ADMIN: '/dashboard',
              CONTENTMANAGER: '/dashboard',
              CONTENTDIRECTOR: '/dashboard',
              WRITER: '/titlesWriter',
              TRANSLATOR: '/titlesWriter',
              REVIEWER: '/titlesWriter',
              EDITOR: '/titlesWriter',
            }}
          />

          <Route path="/login" component={Login} />
          <Route path="/subscribe" component={Subscribe} />

          <PrivateRoute path="/writers" role="ADMIN" component={Writers} />
          <PrivateRoute path="/users" role="ADMIN" component={Users} />

          <PrivateRoute
            path="/dashboard"
            role={CONTENT_MANAGER_ROLES}
            component={Dashboard}
          />
          <PrivateRoute
            path="/titlesContent"
            role={CONTENT_MANAGER_ROLES}
            component={TitlesContent}
          />
          <PrivateRoute
            path="/categories"
            role={CONTENT_MANAGER_ROLES}
            component={Categories}
          />
          <PrivateRoute
            path="/contents"
            role={CONTENT_MANAGER_ROLES}
            component={Contents}
          />
          <PrivateRoute
            path="/productTypes"
            role={CONTENT_MANAGER_ROLES}
            component={ProductTypes}
          />
          <PrivateRoute
            path="/products"
            role={CONTENT_MANAGER_ROLES}
            component={Products}
          />
          <PrivateRoute
            path="/brands"
            role={CONTENT_MANAGER_ROLES}
            component={Brands}
          />
          <PrivateRoute
            path="/reports"
            role={CONTENT_MANAGER_ROLES}
            component={Reports}
          />
          <PrivateRoute
            path="/tags"
            role={CONTENT_MANAGER_ROLES}
            component={Tags}
          />
          <PrivateRoute
            path="/sitePages"
            role={CONTENT_MANAGER_ROLES}
            component={SitePages}
          />
          <PrivateRoute
            path="/siteCompliance"
            role={CONTENT_MANAGER_ROLES}
            component={SiteCompliance}
          />

          <PrivateRoute
            path="/siteSettings"
            role={CONTENT_MANAGER_ROLES}
            component={SiteSettings}
          />

          <PrivateRoute
            path="/titlesWriter"
            role={WRITERS_ROLES}
            component={TitlesWriter}
          />

          <Route component={NotFound} />
        </Switch>
      </HashRouter>
    </RoleContext.Provider>
  );
};

export default Routes;
